import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { queryKey } from 'constants/queryKey';
import PostApi from 'api/post';
import { useToast } from 'app/hooks/useToast';
import { ApiClientError } from 'api/client';
import { useInvalidatePostsQueries } from 'app/modules/posts/hooks/useInvalidatePostsQueries';

export const useTrashMutations = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const invalidatePostsQueries = useInvalidatePostsQueries();

  const invalidateTrashQueries = () => {
    queryClient.invalidateQueries(queryKey.trashedPosts());
    queryClient.invalidateQueries(queryKey.trashedPostsCount());
    invalidatePostsQueries();
  };

  const restoreFromTrash = useMutation(
    (postId: number) => PostApi.restoreFromTrash(postId),
    {
      onSuccess: () => {
        invalidateTrashQueries();
        toast(
          t('postRestoredFromTrash', 'Post restored from trash'),
          'success',
        );
      },
      onError: (e: ApiClientError) => {
        toast(e?.userMessage ?? t('somethingWentWrong'), 'error');
      },
    },
  );

  const permanentDeleteFromTrash = useMutation(
    (postId: number) => PostApi.permanentDeleteFromTrash(postId),
    {
      onSuccess: () => {
        invalidateTrashQueries();
        toast(
          t('postPermanentlyDeleted', 'Post permanently deleted'),
          'success',
        );
      },
      onError: (e: ApiClientError) => {
        toast(e?.userMessage ?? t('somethingWentWrong'), 'error');
      },
    },
  );

  const restoreSelectedFromTrash = useMutation(
    (postIds: number[]) => PostApi.restoreSelectedFromTrash(postIds),
    {
      onSuccess: (data) => {
        invalidateTrashQueries();
        if (data.restoredCount > 0) {
          toast(
            t('postsRestoredFromTrash', '{{count}} posts restored from trash', {
              count: data.restoredCount,
            }),
            'success',
          );
        }
        if (data.failedCount > 0) {
          toast(
            t('somePostsFailedToRestore', '{{count}} posts failed to restore', {
              count: data.failedCount,
            }),
            'warning',
          );
        }
      },
      onError: (e: ApiClientError) => {
        toast(e?.userMessage ?? t('somethingWentWrong'), 'error');
      },
    },
  );

  const permanentDeleteSelectedFromTrash = useMutation(
    (postIds: number[]) => PostApi.permanentDeleteSelectedFromTrash(postIds),
    {
      onSuccess: (data) => {
        invalidateTrashQueries();
        if (data.deletedCount > 0) {
          toast(
            t(
              'postsPermanentlyDeleted',
              '{{count}} posts permanently deleted',
              {
                count: data.deletedCount,
              },
            ),
            'success',
          );
        }
        if (data.failedCount > 0) {
          toast(
            t('somePostsFailedToDelete', '{{count}} posts failed to delete', {
              count: data.failedCount,
            }),
            'warning',
          );
        }
      },
      onError: (e: ApiClientError) => {
        toast(e?.userMessage ?? t('somethingWentWrong'), 'error');
      },
    },
  );

  const moveToTrash = useMutation(
    (postId: number) => PostApi.moveToTrash(postId),
    {
      onSuccess: () => {
        invalidateTrashQueries();
        toast(t('postMovedToTrash', 'Post moved to trash'), 'success');
      },
      onError: (e: ApiClientError) => {
        toast(e?.userMessage ?? t('somethingWentWrong'), 'error');
      },
    },
  );

  return {
    restoreFromTrash,
    permanentDeleteFromTrash,
    restoreSelectedFromTrash,
    permanentDeleteSelectedFromTrash,
    moveToTrash,
    invalidateTrashQueries,
  };
};
